import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: ${props => props.radius || '8px'};
`;

const CardSkeletonContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${props => props.padding || '16px'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: ${props => props.margin || '16px'};
`;

const SkeletonLine = styled(SkeletonBase)`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '16px'};
  margin-bottom: ${props => props.mb || '12px'};
`;

const SkeletonCircle = styled(SkeletonBase)`
  width: ${props => props.size || '40px'};
  height: ${props => props.size || '40px'};
  border-radius: 50%;
`;

const SkeletonRect = styled(SkeletonBase)`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '200px'};
`;

// Ad Card Skeleton for grid view
const AdCardSkeleton = () => (
  <CardSkeletonContainer>
    <SkeletonRect height="200px" mb="12px" />
    <SkeletonLine width="80%" height="20px" mb="8px" />
    <SkeletonLine width="60%" height="16px" mb="8px" />
    <SkeletonLine width="40%" height="24px" mb="8px" />
    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
      <SkeletonLine width="30%" height="14px" mb="0" />
      <SkeletonLine width="30%" height="14px" mb="0" />
      <SkeletonLine width="30%" height="14px" mb="0" />
    </div>
  </CardSkeletonContainer>
);

// List item skeleton
const ListItemSkeleton = () => (
  <CardSkeletonContainer padding="16px">
    <div style={{ display: 'flex', gap: '16px' }}>
      <SkeletonRect width="120px" height="120px" />
      <div style={{ flex: 1 }}>
        <SkeletonLine width="70%" height="20px" mb="8px" />
        <SkeletonLine width="90%" height="14px" mb="8px" />
        <SkeletonLine width="50%" height="24px" mb="12px" />
        <div style={{ display: 'flex', gap: '8px' }}>
          <SkeletonLine width="80px" height="32px" mb="0" />
          <SkeletonLine width="80px" height="32px" mb="0" />
        </div>
      </div>
    </div>
  </CardSkeletonContainer>
);

// User profile skeleton
const UserProfileSkeleton = () => (
  <CardSkeletonContainer>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
      <SkeletonCircle size="60px" />
      <div style={{ flex: 1 }}>
        <SkeletonLine width="40%" height="18px" mb="8px" />
        <SkeletonLine width="60%" height="14px" mb="0" />
      </div>
    </div>
    <SkeletonLine width="100%" height="14px" mb="8px" />
    <SkeletonLine width="80%" height="14px" mb="8px" />
    <SkeletonLine width="90%" height="14px" mb="0" />
  </CardSkeletonContainer>
);

// Grid of ad cards skeleton
const AdGridSkeleton = ({ count = 6 }) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
    padding: '20px 0'
  }}>
    {Array.from({ length: count }).map((_, index) => (
      <AdCardSkeleton key={index} />
    ))}
  </div>
);

// List skeleton
const ListSkeleton = ({ count = 5 }) => (
  <div>
    {Array.from({ length: count }).map((_, index) => (
      <ListItemSkeleton key={index} />
    ))}
  </div>
);

// Simple text skeleton
const TextSkeleton = ({ width = '100%', height = '16px', lines = 1 }) => (
  <div>
    {Array.from({ length: lines }).map((_, index) => (
      <SkeletonLine 
        key={index} 
        width={index === lines - 1 ? '70%' : width}
        height={height} 
        mb={index < lines - 1 ? '8px' : '0'}
      />
    ))}
  </div>
);

export {
  AdCardSkeleton,
  ListItemSkeleton,
  UserProfileSkeleton,
  AdGridSkeleton,
  ListSkeleton,
  TextSkeleton
};

